import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchObjectives,
  fetchObjectiveTree,
  fetchOKRCycles,
  createObjective,
  updateObjective,
  updateObjectiveStatus,
  deleteObjective,
  setViewMode,
  setFilters,
  updateFilter,
  clearFilters,
  setCurrentObjective,
} from '@/store/slices/okrsSlice';
import { fetchInvestibleStructures } from '@/store/slices/investibleStructuresSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { fetchEpics } from '@/store/slices/epicsSlice';

// Components
import {
  GlassCard,
  GlassPageHeader,
  GlassBadge,
  GlassButton,
  GlassSelect,
  GlassModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
} from '@/design-system';
import OKRForm from './components/OKRForm';
import OKRTreeView from './components/OKRTreeView';
import OKRDashboard from './components/OKRDashboard';
import OKRObjectiveCard from './components/OKRObjectiveCard';
import KeyResultProgressModal from './components/KeyResultProgressModal';
import ObjectiveDetailModal from './components/ObjectiveDetailModal';

// Icons
import {
  Target,
  TreePine,
  List,
  Plus,
  Filter,
  BarChart3,
  RefreshCw,
  ChevronDown,
  X,
} from 'lucide-react';

// Types
import type { Objective, KeyResult, OKRLevel, OKRStatusNew, OKRCycle } from '@/types';

const QUARTERS = [
  { value: '', label: 'All Quarters' },
  { value: '1', label: 'Q1' },
  { value: '2', label: 'Q2' },
  { value: '3', label: 'Q3' },
  { value: '4', label: 'Q4' },
];

const LEVELS: { value: OKRLevel | ''; label: string }[] = [
  { value: '', label: 'All Levels' },
  { value: 'IS', label: 'IS Level' },
  { value: 'PROGRAM', label: 'Program Level' },
  { value: 'EPIC', label: 'EPIC Level' },
];

const STATUSES: { value: OKRStatusNew | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT' as OKRStatusNew, label: 'Draft' },
  { value: 'ACTIVE' as OKRStatusNew, label: 'Active' },
  { value: 'COMPLETED' as OKRStatusNew, label: 'Completed' },
  { value: 'CANCELLED' as OKRStatusNew, label: 'Cancelled' },
];

const getCurrentFiscalYear = () => {
  const now = new Date();
  const month = now.getMonth();
  // Fiscal year starts in October (month 9)
  return month >= 9 ? now.getFullYear() + 1 : now.getFullYear();
};

const getCurrentQuarter = () => {
  const now = new Date();
  const month = now.getMonth();
  // Q1: Oct-Dec, Q2: Jan-Mar, Q3: Apr-Jun, Q4: Jul-Sep
  if (month >= 9) return 1;
  if (month >= 0 && month <= 2) return 2;
  if (month >= 3 && month <= 5) return 3;
  return 4;
};

const OKRsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const {
    objectives,
    objectiveTree,
    cycles,
    currentCycle,
    filters,
    viewMode,
    isLoading,
    isLoadingTree,
    error,
    pagination,
  } = useSelector((state: RootState) => state.okrs);

  const { structures: investibleStructures } = useSelector((state: RootState) => state.investibleStructures);
  const { programs } = useSelector((state: RootState) => state.programs);
  const { epics } = useSelector((state: RootState) => state.epics);

  // Local state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [selectedKeyResult, setSelectedKeyResult] = useState<KeyResult | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  // Initialize filter values
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<string>(getCurrentFiscalYear().toString());
  const [selectedQuarter, setSelectedQuarter] = useState<string>(getCurrentQuarter().toString());
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Generate fiscal year options
  const fiscalYearOptions = [
    { value: '', label: 'All Years' },
    ...Array.from({ length: 7 }, (_, i) => ({
      value: (getCurrentFiscalYear() - 3 + i).toString(),
      label: `FY${(getCurrentFiscalYear() - 3 + i).toString().slice(-2)}`,
    })),
  ];

  // Load initial data
  useEffect(() => {
    dispatch(fetchOKRCycles());
    dispatch(fetchInvestibleStructures({}));
    dispatch(fetchPrograms({}));
    dispatch(fetchEpics({}));
  }, [dispatch]);

  // Fetch objectives based on filters
  const fetchData = useCallback(() => {
    const params: Record<string, any> = {};
    if (selectedFiscalYear) params.fiscalYear = parseInt(selectedFiscalYear);
    if (selectedQuarter) params.quarter = parseInt(selectedQuarter);
    if (selectedLevel) params.level = selectedLevel;
    if (selectedStatus) params.status = selectedStatus;

    if (viewMode === 'tree') {
      dispatch(fetchObjectiveTree(params));
    } else {
      dispatch(fetchObjectives(params));
    }
  }, [dispatch, viewMode, selectedFiscalYear, selectedQuarter, selectedLevel, selectedStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFiscalYearChange = (value: string) => {
    setSelectedFiscalYear(value);
  };

  const handleQuarterChange = (value: string) => {
    setSelectedQuarter(value);
  };

  const handleLevelChange = (value: string) => {
    setSelectedLevel(value);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const handleClearFilters = () => {
    setSelectedFiscalYear('');
    setSelectedQuarter('');
    setSelectedLevel('');
    setSelectedStatus('');
  };

  // View mode toggle
  const handleViewModeChange = (mode: 'list' | 'tree') => {
    dispatch(setViewMode(mode));
  };

  // CRUD handlers
  const handleCreate = async (data: any) => {
    try {
      await dispatch(createObjective(data)).unwrap();
      setIsCreateModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Failed to create objective:', err);
    }
  };

  const handleUpdate = async (data: any) => {
    if (!selectedObjective) return;
    try {
      await dispatch(updateObjective({ id: selectedObjective.id, data })).unwrap();
      setIsEditModalOpen(false);
      setSelectedObjective(null);
      fetchData();
    } catch (err) {
      console.error('Failed to update objective:', err);
    }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await dispatch(updateObjectiveStatus({ id, status })).unwrap();
      fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this objective?')) return;
    try {
      await dispatch(deleteObjective(id)).unwrap();
      fetchData();
    } catch (err) {
      console.error('Failed to delete objective:', err);
    }
  };

  // Detail and edit handlers
  const handleViewDetails = (objective: Objective) => {
    setSelectedObjective(objective);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (objective: Objective) => {
    setSelectedObjective(objective);
    setIsEditModalOpen(true);
  };

  const handleProgressUpdate = (keyResult: KeyResult) => {
    setSelectedKeyResult(keyResult);
    setIsProgressModalOpen(true);
  };

  // Status badge helper
  const getStatusColor = (status: OKRStatusNew) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'info';
      case 'DRAFT':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Level badge helper
  const getLevelColor = (level?: OKRLevel) => {
    switch (level) {
      case 'IS':
        return 'blue';
      case 'PROGRAM':
        return 'blue';
      case 'EPIC':
        return 'green';
      default:
        return 'default';
    }
  };

  const hasActiveFilters = selectedFiscalYear || selectedQuarter || selectedLevel || selectedStatus;

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Objectives & Key Results"
          subtitle="Track and manage multi-level OKRs across your portfolio"
          icon={<Target className="w-6 h-6" />}
          iconGradient="emerald"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <GlassButton
                variant="ghost"
                onClick={() => setIsDashboardOpen(true)}
                leftIcon={<BarChart3 className="w-4 h-4" />}
              >
                Dashboard
              </GlassButton>
              <GlassButton
                variant="ghost"
                onClick={fetchData}
                disabled={isLoading || isLoadingTree}
                leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading || isLoadingTree ? 'animate-spin' : ''}`} />}
              >
                Refresh
              </GlassButton>
              <GlassButton
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                New Objective
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      <div className="space-y-6">
        {/* View Toggle and Filters Toggle */}
        <SectionReveal delay={0.1}>
          <div className="flex items-center justify-between">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 p-1 bg-surface-glass rounded-lg border border-border-subtle">
              <button
                onClick={() => handleViewModeChange('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'list'
                    ? 'bg-accent-blue/20 text-accent-blue'
                    : 'text-muted-foreground/60 hover:text-foreground hover:bg-surface-glass-hover'
                }`}
              >
                <List className="w-4 h-4" />
                List View
              </button>
              <button
                onClick={() => handleViewModeChange('tree')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'tree'
                    ? 'bg-accent-blue/20 text-accent-blue'
                    : 'text-muted-foreground/60 hover:text-foreground hover:bg-surface-glass-hover'
                }`}
              >
                <TreePine className="w-4 h-4" />
                Tree View
              </button>
            </div>

            {/* Filter Toggle */}
            <GlassButton
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="w-4 h-4" />}
              rightIcon={<ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />}
            >
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 bg-accent-blue rounded-full" />
              )}
            </GlassButton>
          </div>
        </SectionReveal>

        {/* Filters Panel */}
        {showFilters && (
          <SectionReveal delay={0.15}>
            <GlassCard padding="md">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <GlassSelect
                  label="Fiscal Year"
                  value={selectedFiscalYear}
                  onChange={handleFiscalYearChange}
                  options={fiscalYearOptions}
                  size="sm"
                />
                <GlassSelect
                  label="Quarter"
                  value={selectedQuarter}
                  onChange={handleQuarterChange}
                  options={QUARTERS}
                  size="sm"
                />
                <GlassSelect
                  label="Level"
                  value={selectedLevel}
                  onChange={handleLevelChange}
                  options={LEVELS}
                  size="sm"
                />
                <GlassSelect
                  label="Status"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  options={STATUSES}
                  size="sm"
                />
                {hasActiveFilters && (
                  <div className="flex items-end">
                    <GlassButton
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      leftIcon={<X className="w-4 h-4" />}
                    >
                      Clear
                    </GlassButton>
                  </div>
                )}
              </div>
            </GlassCard>
          </SectionReveal>
        )}

        {/* Error Display */}
        {error && (
          <SectionReveal>
            <GlassAlert variant="error" message={error} className="mb-6" />
          </SectionReveal>
        )}

        {/* Loading State */}
        {(isLoading || isLoadingTree) && (
          <SectionReveal delay={0.2}>
            <GlassCard padding="lg">
              <div className="flex items-center justify-center py-12">
                <GlassLoadingSpinner size="lg" variant="gradient" label="Loading objectives..." />
              </div>
            </GlassCard>
          </SectionReveal>
        )}

        {/* Content Area */}
        {!isLoading && !isLoadingTree && (
          <SectionReveal delay={0.2}>
            {viewMode === 'list' ? (
              <div className="space-y-4">
                {objectives.length === 0 ? (
                  <GlassCard padding="lg">
                    <div className="p-12 text-center">
                      <Target className="w-12 h-12 text-muted-foreground/60 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No objectives found</h3>
                      <p className="text-muted-foreground mb-4">
                        {hasActiveFilters
                          ? 'Try adjusting your filters to see more objectives.'
                          : 'Create your first objective to get started with OKR tracking.'}
                      </p>
                      <GlassButton
                        variant="primary"
                        onClick={() => setIsCreateModalOpen(true)}
                        leftIcon={<Plus className="w-4 h-4" />}
                      >
                        Create Objective
                      </GlassButton>
                    </div>
                  </GlassCard>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground/60 mb-2">
                      Showing {objectives.length} objective{objectives.length !== 1 ? 's' : ''}
                    </div>
                    {objectives.map((objective) => (
                      <OKRObjectiveCard
                        key={objective.id}
                        objective={objective}
                        onView={handleViewDetails}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onStatusChange={handleStatusUpdate}
                        onProgressUpdate={handleProgressUpdate}
                        getLevelColor={getLevelColor}
                        getStatusColor={getStatusColor}
                      />
                    ))}
                  </>
                )}
              </div>
            ) : (
              <OKRTreeView
                tree={objectiveTree}
                onViewDetails={handleViewDetails}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onStatusChange={handleStatusUpdate}
                onProgressUpdate={handleProgressUpdate}
                getLevelColor={getLevelColor}
                getStatusColor={getStatusColor}
              />
            )}
          </SectionReveal>
        )}
      </div>

      {/* Create Objective Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Objective"
        subtitle="Define a new objective with key results"
        icon={<Plus className="w-5 h-5" />}
        iconGradient="emerald"
        size="lg"
      >
        <OKRForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          investibleStructures={investibleStructures}
          programs={programs}
          epics={epics}
          existingObjectives={objectives}
          defaultFiscalYear={selectedFiscalYear ? parseInt(selectedFiscalYear) : getCurrentFiscalYear()}
          defaultQuarter={selectedQuarter ? parseInt(selectedQuarter) : getCurrentQuarter()}
        />
      </GlassModal>

      {/* Edit Objective Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedObjective(null);
        }}
        title="Edit Objective"
        subtitle={selectedObjective?.title}
        icon={<Target className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        {selectedObjective && (
          <OKRForm
            initialData={selectedObjective}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedObjective(null);
            }}
            investibleStructures={investibleStructures}
            programs={programs}
            epics={epics}
            existingObjectives={objectives.filter((o) => o.id !== selectedObjective.id)}
          />
        )}
      </GlassModal>

      {/* Objective Detail Modal */}
      <GlassModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedObjective(null);
        }}
        title="Objective Details"
        subtitle={selectedObjective?.title}
        icon={<Target className="w-5 h-5" />}
        iconGradient="emerald"
        size="xl"
      >
        {selectedObjective && (
          <ObjectiveDetailModal
            objective={selectedObjective}
            onEdit={() => {
              setIsDetailModalOpen(false);
              setIsEditModalOpen(true);
            }}
            onClose={() => {
              setIsDetailModalOpen(false);
              setSelectedObjective(null);
            }}
            onProgressUpdate={handleProgressUpdate}
            getLevelColor={getLevelColor}
            getStatusColor={getStatusColor}
          />
        )}
      </GlassModal>

      {/* Key Result Progress Modal */}
      <GlassModal
        isOpen={isProgressModalOpen}
        onClose={() => {
          setIsProgressModalOpen(false);
          setSelectedKeyResult(null);
        }}
        title="Update Progress"
        subtitle="Track progress for this key result"
        icon={<BarChart3 className="w-5 h-5" />}
        iconGradient="cyan"
        size="md"
      >
        {selectedKeyResult && (
          <KeyResultProgressModal
            keyResult={selectedKeyResult}
            onClose={() => {
              setIsProgressModalOpen(false);
              setSelectedKeyResult(null);
              fetchData();
            }}
          />
        )}
      </GlassModal>

      {/* Dashboard Modal */}
      <GlassModal
        isOpen={isDashboardOpen}
        onClose={() => setIsDashboardOpen(false)}
        title="OKR Dashboard"
        subtitle="Overview of objectives and key results performance"
        icon={<BarChart3 className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="xl"
      >
        <OKRDashboard
          fiscalYear={selectedFiscalYear ? parseInt(selectedFiscalYear) : undefined}
          quarter={selectedQuarter ? parseInt(selectedQuarter) : undefined}
          onClose={() => setIsDashboardOpen(false)}
        />
      </GlassModal>
    </div>
  );
};

export default OKRsPage;
